import req from '@sportninja/common/api/request'
import { useDebounce } from '@sportninja/common/hooks/useDebounce'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { useCallback, useEffect, useState } from 'react'

const useOrgList = (
  slug,
  {
    initialSort = 'name',
    initialDirection = 'asc',
    initialMembershipType = 'both',
    externalMembershipType = null,
    onMembershipTypeChangeExternal = null,
  } = {}
) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(initialSort)
  const [direction, setDirection] = useState(initialDirection)
  const [totalCount, setTotalCount] = useState(0)
  const [permissions, setPermissions] = useState({})
  const [error, setError] = useState(null)
  const [internalMembershipType, setInternalMembershipType] = useState(
    initialMembershipType
  )

  // Use external value if provided (controlled mode), otherwise internal state
  const membershipType = externalMembershipType ?? internalMembershipType

  const debouncedSearch = useDebounce(search, 500)

  const fetchItems = useCallback(
    async (
      currentPage,
      currentSort,
      currentDirection,
      currentSearch,
      currentMembershipType,
      isAppend = false
    ) => {
      if (!slug) {
        return
      }

      setError(null)
      setLoading(true)
      try {
        const query = {
          page: currentPage,
          sort: currentSort,
          direction: currentDirection,
          membership_type: currentMembershipType,
          ...(currentSearch && { query: currentSearch }),
        }

        const response = await req(slug, { query })

        const newItems = response.data || []
        const meta = response.meta || {}
        const pagination = meta.pagination || {}
        const newPermissions = meta.permissions || {}

        setPermissions((prev) =>
          isAppend ? { ...prev, ...newPermissions } : newPermissions
        )
        setItems((prev) => (isAppend ? [...prev, ...newItems] : newItems))
        setTotalCount(pagination.total || 0)

        const totalPages = pagination.total_pages || 1
        setHasMore(currentPage < totalPages)
      } catch (e) {
        const errorMessage = getErrorMessage(e)
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    },
    [slug]
  )

  // Initial load and search/sort changes
  useEffect(() => {
    if (!slug) {
      return
    }
    setPage(1)
    fetchItems(1, sort, direction, debouncedSearch, membershipType, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, direction, debouncedSearch, slug, membershipType])

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchItems(
        nextPage,
        sort,
        direction,
        debouncedSearch,
        membershipType,
        true
      )
    }
  }, [
    loading,
    hasMore,
    page,
    sort,
    direction,
    debouncedSearch,
    membershipType,
    fetchItems,
  ])

  const handleSearch = useCallback((value) => {
    setSearch(value)
  }, [])

  const handleSort = useCallback((newSort, newDirection) => {
    setSort(newSort)
    setDirection(newDirection)
  }, [])

  const handleMembershipType = useCallback(
    (value) => {
      setInternalMembershipType(value)
      onMembershipTypeChangeExternal?.(value)
    },
    [onMembershipTypeChangeExternal]
  )

  const refresh = useCallback(() => {
    setPage(1)
    fetchItems(1, sort, direction, debouncedSearch, membershipType, false)
  }, [sort, direction, debouncedSearch, membershipType, fetchItems])

  return {
    items,
    loading,
    hasMore,
    totalCount,
    permissions,
    search,
    membershipType,
    handleSearch,
    handleSort,
    handleMembershipType,
    loadMore,
    refresh,
    sort,
    direction,
    error,
  }
}

export default useOrgList
